/**
 * Analytics vocabulary for the participant list triage UI.
 *
 * Everything that turns UI state into Mixpanel properties lives here so the list events
 * and the events fired elsewhere (chat_send, mark_as_unread) can't drift apart. Property
 * names and values are the wire contract and are deliberately snake_case, hence the
 * camelcase exemption below.
 *
 * Nothing here may carry PHI/PII: no names, emails, uids, tag labels, scores or raw day
 * counts. Tags are counted, never named.
 */
/* eslint-disable camelcase */
import type { ParticipantListSnapshot } from 'src/mixpanel/participantListContext'
import { shouldShowCallTodayIndicator } from './ParticipantListItem/callTodayUtils'
import { isViewedToday } from './ParticipantListItem/viewedTodayUtils'
import {
  PriorityFilter,
  PriorityFilterCounts,
  getParticipantPriorityBands,
  isParticipantOnTrack,
} from './priorityFilterUtils'
import { RoleFilter } from './roleFilterUtils'
import { HomeViewParticipantFragment } from './searchParticipants.generated'

type Participant = HomeViewParticipantFragment

/** The code says `reEngage`; the wire says `re_engage`. */
export const PRIORITY_WIRE_VALUES = {
  safety: 'safety',
  reEngage: 're_engage',
  followUp: 'follow_up',
  waiting: 'waiting',
  onTrack: 'on_track',
} as const satisfies Record<PriorityFilter, string>

export type PriorityWireValue = (typeof PRIORITY_WIRE_VALUES)[PriorityFilter]

export const toPriorityWireValues = (priorities: ReadonlyArray<PriorityFilter>): Array<PriorityWireValue> =>
  priorities.map((priority) => PRIORITY_WIRE_VALUES[priority])

export const ROLE_WIRE_VALUES = {
  primaryProvider: 'primary_provider',
  otherCareTeamMember: 'other_care_team_member',
} as const satisfies Record<RoleFilter, string>

export type RoleWireValue = (typeof ROLE_WIRE_VALUES)[RoleFilter]

export const toRoleWireValues = (roles: ReadonlyArray<RoleFilter>): Array<RoleWireValue> =>
  roles.map((role) => ROLE_WIRE_VALUES[role])

/** Matches the indicator chips rendered by `ParticipantListItem/PriorityIndicators`. */
export const PARTICIPANT_INDICATORS = [
  'risk',
  'days_disengaged',
  'hq_overdue',
  'pending_doc',
  'call_follow_up',
  'call_today',
  'unread',
  'draft',
] as const

export type ParticipantIndicator = (typeof PARTICIPANT_INDICATORS)[number]

export const getParticipantIndicators = (
  participant: Participant,
  hasDraftMessage: boolean
): Array<ParticipantIndicator> => {
  const { indicators } = participant
  const shown: Record<ParticipantIndicator, unknown> = {
    risk: indicators?.risk,
    days_disengaged: indicators?.daysDisengaged,
    hq_overdue: indicators?.overdueHealthQuestionnaire,
    pending_doc: indicators?.monthlySummaryNotes?.showIndicator,
    call_follow_up: indicators?.completedCallAwaitingNote,
    call_today: shouldShowCallTodayIndicator(indicators?.currentOrUpcomingCall),
    unread: indicators?.newMessages?.count,
    draft: hasDraftMessage,
  }
  return PARTICIPANT_INDICATORS.filter((indicator) => Boolean(shown[indicator]))
}

export const getParticipantBands = (participant: Participant, hasDraftMessage: boolean): Array<PriorityWireValue> =>
  toPriorityWireValues(getParticipantPriorityBands(participant, hasDraftMessage))

/**
 * Participants in nobody's triage bucket: no priority indicator and no tags. Always
 * computed over the full caseload, never over the filtered result set.
 */
export const countCaseload = (participants: ReadonlyArray<Participant>, uidsWithDraftMessage: Set<string>): number =>
  participants.filter(
    (participant) =>
      (participant.tags?.length ?? 0) === 0 &&
      isParticipantOnTrack(participant, uidsWithDraftMessage.has(participant.uid))
  ).length

export type FilterState = {
  priorities: ReadonlyArray<PriorityFilter>
  tagCount: number
  roles: ReadonlyArray<RoleFilter>
}

export type FilterAnalyticsProps = {
  priority_filter: Array<PriorityWireValue>
  priority_filter_count: number
  tag_filter_count: number
  role_filter: Array<RoleWireValue>
}

export const buildFilterProps = ({ priorities, tagCount, roles }: FilterState): FilterAnalyticsProps => ({
  priority_filter: toPriorityWireValues(priorities),
  priority_filter_count: priorities.length,
  tag_filter_count: tagCount,
  role_filter: toRoleWireValues(roles),
})

export type ParticipantListViewedProps = FilterAnalyticsProps & {
  result_count: number
  caseload_count: number
  safety_count: number
  re_engage_count: number
  follow_up_count: number
  waiting_count: number
  on_track_count: number
}

export const buildParticipantListViewedProps = (args: {
  filters: FilterState
  resultCount: number
  caseloadCount: number
  /** The badge numbers on the pills — indicator counts, except On track, which counts participants. */
  priorityCounts: PriorityFilterCounts
}): ParticipantListViewedProps => ({
  ...buildFilterProps(args.filters),
  result_count: args.resultCount,
  caseload_count: args.caseloadCount,
  safety_count: args.priorityCounts.safety,
  re_engage_count: args.priorityCounts.reEngage,
  follow_up_count: args.priorityCounts.followUp,
  waiting_count: args.priorityCounts.waiting,
  on_track_count: args.priorityCounts.onTrack,
})

/** The pill that was clicked and whether the click selected or deselected it. */
export type PrioritySelectionChange = {
  priority: PriorityFilter
  selected: boolean
}

export type PriorityFilterToggledProps = FilterAnalyticsProps & {
  priority: PriorityWireValue
  selected: boolean
  count_shown: number
}

export const buildPriorityFilterToggledProps = (args: {
  change: PrioritySelectionChange
  /** The full selection *after* the click. */
  priorities: ReadonlyArray<PriorityFilter>
  tagCount: number
  roles: ReadonlyArray<RoleFilter>
  priorityCounts: PriorityFilterCounts
}): PriorityFilterToggledProps => ({
  ...buildFilterProps({ priorities: args.priorities, tagCount: args.tagCount, roles: args.roles }),
  priority: PRIORITY_WIRE_VALUES[args.change.priority],
  selected: args.change.selected,
  count_shown: args.priorityCounts[args.change.priority],
})

export type TagFilterAction = 'add' | 'remove' | 'clear'

export type TagsFilterChangedProps = FilterAnalyticsProps & {
  action: TagFilterAction
}

export const buildTagsFilterChangedProps = (args: {
  action: TagFilterAction
  /** Filter state *after* the change. */
  filters: FilterState
}): TagsFilterChangedProps => ({
  ...buildFilterProps(args.filters),
  action: args.action,
})

/** The checkbox that was toggled, or Clear (which only fires when a role was selected). */
export type RoleSelectionChange = { action: 'add' | 'remove'; role: RoleFilter } | { action: 'clear' }

export type RoleFilterChangedProps = FilterAnalyticsProps & {
  action: RoleSelectionChange['action']
  role?: RoleWireValue
}

export const buildRoleFilterChangedProps = (args: {
  change: RoleSelectionChange
  /** Filter state *after* the change. */
  filters: FilterState
}): RoleFilterChangedProps => ({
  ...buildFilterProps(args.filters),
  action: args.change.action,
  ...(args.change.action === 'clear' ? {} : { role: ROLE_WIRE_VALUES[args.change.role] }),
})

/**
 * Which way a layout control was clicked, named by the direction the click takes the list:
 * `show`/`hide` for the sidebar icon, `expand`/`shrink` for the chevrons.
 */
export type ParticipantListLayoutAction = 'show' | 'hide' | 'expand' | 'shrink'

export type ParticipantListLayoutChangedProps = FilterAnalyticsProps & {
  action: ParticipantListLayoutAction
}

/** The patient message marked as unread, named after its feed item type. */
export type MarkAsUnreadEntryType = 'chat_message' | 'sms_message'

export type MarkAsUnreadProps = FilterAnalyticsProps & {
  entry_type: MarkAsUnreadEntryType
}

export type ParticipantOpenedProps = FilterAnalyticsProps & {
  patient_priority_bands: Array<PriorityWireValue>
  indicators_present: Array<ParticipantIndicator>
  viewed_today: boolean
}

export const buildParticipantOpenedProps = (args: {
  participant: Participant
  hasDraftMessage: boolean
  filters: FilterState
}): ParticipantOpenedProps => ({
  ...buildFilterProps(args.filters),
  patient_priority_bands: getParticipantBands(args.participant, args.hasDraftMessage),
  indicators_present: getParticipantIndicators(args.participant, args.hasDraftMessage),
  viewed_today: isViewedToday(args.participant.indicators?.lastViewedAt),
})

export type ChatContextProps = FilterAnalyticsProps & {
  /**
   * Absent when the open participant is not in the list (e.g. a deep link to someone
   * outside the home view). A listed participant always has at least one band, `on_track`
   * when no other applies.
   */
  patient_priority_bands?: Array<PriorityWireValue>
}

/**
 * Live fallback for `chat_send` when no bands were captured for the visit (see
 * `getChatSendProps`). The draft indicator is left out here: on `chat_send` the draft is the
 * message being sent, so counting it would put every participant in the Waiting band; a
 * participant whose only signal is that draft reads as `on_track`.
 */
export const buildChatContextProps = (args: {
  filters: FilterState
  selectedParticipant?: Participant
}): ChatContextProps => ({
  ...buildFilterProps(args.filters),
  ...(args.selectedParticipant ? { patient_priority_bands: getParticipantBands(args.selectedParticipant, false) } : {}),
})

export const buildParticipantListSnapshot = (args: {
  filters: FilterState
  selectedParticipant?: Participant
  hasDraftMessage: boolean
}): ParticipantListSnapshot => ({
  filterProps: buildFilterProps(args.filters),
  chatSendProps: buildChatContextProps(args),
  ...(args.selectedParticipant
    ? {
        participantOpened: {
          uid: args.selectedParticipant.uid,
          props: buildParticipantOpenedProps({
            participant: args.selectedParticipant,
            hasDraftMessage: args.hasDraftMessage,
            filters: args.filters,
          }),
        },
      }
    : {}),
})
